import type { ReactNode } from "react";
import React from "react";
import type fr from "./fr";

const en: typeof fr = {
  "404.action": "Back to home",
  "404.description": "URL may have changed since you bookmarked it",
  "404.title": (url: string) => (
    <>
      <strong>{url}</strong> not found :(
    </>
  ),
  "badge.beta": "Beta",
  "badge.beta.description":
    "Feature available in early access, still under development and may present limitations.",
  "badge.in_development": "In development",
  "chart.download.error": "An error occurred while downloading .png",
  "chart.download.png": "Download .png",
  "chart.download.success": (filename: string) => `File ${filename} has been exported`,
  "chart.full_viewport.close": "Exit full screen mode",
  "chart.full_viewport.open": "Open in full screen",
  "charts.info_tooltip.more": "Learn more",
  "color_picker.default": "Default color",
  "color_picker.palette": "Custom color",
  "daterangepicker.apply": "Apply",
  "daterangepicker.cancel": "Cancel",
  "daterangepicker.end_time": "End time",
  "daterangepicker.error.dates_unavailable": "Selected dates are not available",
  "daterangepicker.error.startdate_superior_to_enddate": "Start date is greater than end date",
  "daterangepicker.instruction": "↲ enter to apply",
  "daterangepicker.start_time": "Start time",
  "daterangepicker.tooltip.apply": "Apply",
  "daterangepicker.tooltip.clear": "Clear",
  "daterangepicker.tooltip.open_calendar": "Open calendar",
  "detail.summary": "Details",
  "details.open.hint": "Click to expand",
  "drawer.cancel": "Cancel",
  "drawer.confirm": "Confirm",
  "dropzone.drop_here": ({ oneFile }: { oneFile: boolean }): string =>
    oneFile ? "Drop your file here or" : "Drop your files here or",
  "dropzone.select_files": ({ oneFile }: { oneFile: boolean }): string =>
    oneFile ? "Select your file" : "Select your files",
  "dropzoneV2.accepted_files.separator.or": "or",
  "dropzoneV2.expected_file_format": "Expected file format :",
  "dropzoneV2.file_version_detected": `File version detected :`,
  "dropzoneV2.loading": "File being processed",
  "dropzoneV2.max_size": (maxSize: number) => `(Max size: ${maxSize} Mo)`,
  "dropzoneV2.max_size_file.text": (maxSizeInMo: number) =>
    `File size exceeds ${maxSizeInMo} MB limit.`,
  "dropzoneV2.message.no_error_message_defined": "No error message defined",
  "dropzoneV2.no_valid_file.text": ({ nameFile }: { nameFile: ReactNode }) => (
    <>
      The {nameFile} file <br /> is not valid for import.
    </>
  ),
  "dropzoneV2.no_valid_files.text": (n: number) => (
    <>Some files among the {n} added are not valid for import.</>
  ),
  "dropzoneV2.rejectedFiles.message": ({ name, accept }: { name: string; accept: string }) =>
    `File format ${name} is invalid Please import a file of type ${accept}.`,
  "dropzoneV2.sample_File": "Sample file",
  "dropzoneV2.select_files": ({ oneFile }: { oneFile: boolean }): string =>
    oneFile ? "Select it on your computer" : "Select them on your computer",
  "dropzoneV2.tooltip.download_error_message": "Download detailed error message",
  "dropzoneV2.valid.select_files": ({ oneFile }: { oneFile: boolean }): string =>
    oneFile ? "Select or drag a new file" : "Select or drag new files",
  "dropzoneV2.valid_file.missing_info.text": ({ nameFile }: { nameFile: ReactNode }) => (
    <>The file {nameFile} is valid, but some necessary information is missing.</>
  ),
  "dropzoneV2.valid_file.text": ({ nameFile }: { nameFile: ReactNode }) => (
    <>
      The {nameFile} file <br /> is valid for import.
    </>
  ),
  "dropzoneV2.valid_files.missing_info.text": (n: number) => (
    <>The {n} files are valid, but some necessary information is missing</>
  ),
  "dropzoneV2.valid_files.text": (n: number) => `The ${n} files are valid for import.`,
  "filter.none": "None",
  "filter.reset": "Reset",
  "filter.title": (title: string) => `${title}:\u00A0`,
  filters: "Filters",
  "group.no_group": "None",
  "highcharts.tooltip.flag.description": ({
    name,
    description,
  }: {
    name: string;
    description: string;
  }) => `${name}: ${description}`,
  "image_reader.reset": "Reset zoom and position",
  "image_reader.zoom_in": "Zoom in",
  "image_reader.zoom_out": "Zoom out",
  "input.mandatory": "Mandatory",
  "input.revert": "Revert",
  "markdown.table.modal.form.input.error.columns": "The number of columns cannot be empty.",
  "markdown.table.modal.form.input.error.max_columns": "Number of columns cannot exceed 20",
  "markdown.table.modal.form.input.error.max_rows": "The number of lines cannot exceed 20",
  "markdown.table.modal.form.input.error.rows": "The number of lines cannot be empty.",
  "markdown_editor.modal.insert": "Insert",
  "markdown_editor.modal.link.label": "Link",
  "markdown_editor.modal.link.placeholder": "Enter URL (starting with https://)",
  "markdown_editor.plugin.equation.edit_hint": "Double-click to edit the equation",
  "markdown_editor.plugin.equation.input_label": "Equation (in LaTeX)",
  "markdown_editor.plugin.equation.insert": "Insert an equation",
  "markdown_editor.plugin.equation.insert.disable": "Equation not available within tables",
  "markdown_editor.plugin.equation.insert_short": "Insert",
  "markdown_editor.plugin.equation.preview_label": "Preview",
  "markdown_editor.plugin.equation.preview_label.empty": "No equation to preview",
  "markdown_editor.plugin.equation.tooltip.text.description":
    "Examples of LaTeX syntax are available in the user guide.",
  "markdown_editor.plugin.equation.tooltip.text.link": "Link to the dedicated section",
  "markdown_editor.table.modal.form.input.label.columns": "Number of columns",
  "markdown_editor.table.modal.form.input.label.rows": "Number of lines",
  "markdown_editor.table.modal.form.ok_button_name": "Insert",
  "markdown_editor.table.modal.title": "Insert a table",
  "markdown_editor.table.toolbar.button": "Insert a table",
  "markdown_editor.table.toolbar.button.disable": "Table not available within tables",
  "markdown_editor.tool_bar.link.error.invalid_url": "Invalid URL",
  "markdown_editor.tool_bar.link.insert": "Insert link",
  "markdown_editor.tool_bar.link.remove": "Remove link",
  "markdown_editor.tool_bar.link.replace": "Replace link",
  "markdown_editor.tool_bar.tooltip.bold": "Bold",
  "markdown_editor.tool_bar.tooltip.italic": "Italic",
  "markdown_editor.tool_bar.tooltip.table.disable": "Formatting not available within tables",
  "message_card.answer": "Reply",
  "message_card.answers": ({ count }: { count: number }) =>
    count === 1 ? `${count} reply` : `${count} replies`,
  "message_card.ask_delete": "Delete message?",
  "message_card.ask_delete_thread": "Delete the thread?",
  "message_card.cancel": "Cancel",
  "message_card.confirm_delete": "Delete",
  "message_card.delete": "Delete",
  "message_card.delete_thread": "Delete the thread",
  "message_card.edit": "Edit",
  "message_editor.cancel": "Cancel",
  "message_editor.publish": "Publish",
  "modal.cancel": "Cancel",
  "modal.confirm": "Confirm",
  "placeholder.search": "Search",
  "project.dashboard.block.comment_rich_text.error.text_too_long":
    "Text must not exceed 4000 characters",
  "react_select.confirm_delete": (label: string) => `Delete "${label}"?`,
  "react_select.confirm_edit": (label: string) => `Change "${label}" label everywhere?`,
  "react_select.create": (str: string) => `Create "${str}"`,
  "react_select.no_result": "No result",
  "react_select.no_value": "Empty value",
  "rollback.title": "Rollback to previous value",
  search: "Search",
  "select.no_result": "None",
  "selectlist.no_data": "No data",
  "selectlist.selected_count": ({ count }: { count: number }) =>
    count < 2 ? `${count} selected element` : `${count} selected elements`,
  "side_panel.close": "Close panel",
  "side_panel.open": "Open panel",
  "table.columns": "Displayed columns",
  "table.display": "Display",
  "table.double_click_to_edit": "Double-click to edit",
  "table.empty": "No data",
  "table.form.cancel": "Cancel",
  "table.form.confirm": "Confirm",
  "table.groupby": "Group by",
  "table.groupby.none": "None",
  "table.pagination.elementByPage": "Items per page",
  "table.pagination.of": "of",
  "table.select_all": "Select all",
  "table.unselect_all": "Deselect all",
  "textarea.maxLength.exceeded": "(maximum limit has been exceeded)",
  "textarea.maxLength.label": "characters",
  "time_period.days": ({
    days,
    hours,
    minutes,
  }: {
    days: number;
    hours: number;
    minutes: number;
  }) => {
    const daysSection = days > 0 ? `${days} d` : "";
    const hoursSection = hours > 0 || days !== 0 ? `${hours} h` : "";
    return `${daysSection} ${hoursSection} ${minutes} min`;
  },
};

export default en;
